import { uuid } from '../ids.js';
import { applyLocal, applyLocalMany, writeTx } from '../mutate.js';
import { recomputeDecks } from '../../scheduler/recompute.js';

export function liveDecks(decks) {
  return decks.filter((d) => !d.deletedAt);
}

/** Nested tree; decks whose parent is missing or deleted become roots. */
export function buildTree(decks) {
  const live = liveDecks(decks);
  const byId = new Map(live.map((d) => [d.id, { ...d, children: [] }]));
  const roots = [];
  for (const node of byId.values()) {
    const parent = node.parentId ? byId.get(node.parentId) : null;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  const sort = (list) => {
    list.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '', undefined, { numeric: true }));
    list.forEach((n) => sort(n.children));
    return list;
  };
  return sort(roots);
}

export function descendantIds(decks, rootId) {
  const live = liveDecks(decks);
  const children = new Map();
  for (const d of live) {
    if (!children.has(d.parentId)) children.set(d.parentId, []);
    children.get(d.parentId).push(d.id);
  }
  const out = [];
  const stack = [rootId];
  const seen = new Set();
  while (stack.length) {
    const id = stack.pop();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    stack.push(...(children.get(id) ?? []));
  }
  return out;
}

export function deckPath(decks, id) {
  const byId = new Map(decks.map((d) => [d.id, d]));
  const parts = [];
  const seen = new Set();
  let cur = byId.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    parts.unshift(cur.name);
    cur = cur.parentId ? byId.get(cur.parentId) : null;
  }
  return parts.join(' / ');
}

/** Flat, tree-ordered list for <select>s: [{ id, name, depth, path }] */
export function flattenTree(decks) {
  const out = [];
  const walk = (nodes, depth) => {
    for (const n of nodes) {
      out.push({ id: n.id, name: n.name, depth, path: deckPath(decks, n.id) });
      walk(n.children, depth + 1);
    }
  };
  walk(buildTree(decks), 0);
  return out;
}

export async function createDeck(db, { name, parentId = null }) {
  const id = uuid();
  const now = Date.now();
  await applyLocal(db, 'deck', id, {
    name: name.trim() || 'Untitled deck',
    parentId,
    position: now,
    createdAt: now,
    deletedAt: null,
    purged: false,
  });
  return id;
}

export function renameDeck(db, id, name) {
  return applyLocal(db, 'deck', id, { name: name.trim() || 'Untitled deck' });
}

export async function moveDeck(db, id, parentId) {
  if (parentId) {
    const decks = await db.decks.toArray();
    if (descendantIds(decks, id).includes(parentId)) {
      throw new Error('A deck cannot be moved inside itself.');
    }
  }
  return applyLocal(db, 'deck', id, { parentId: parentId || null });
}

export async function updateDeckConfig(db, id, partial) {
  await applyLocal(db, 'deck', id, { config: partial });
  await recomputeDecks(db, [id]);
}

/** Soft-delete a deck, its subdecks, and their notes/cards with one shared timestamp. */
export async function deleteDeck(db, id) {
  const now = Date.now();
  return writeTx(db, async () => {
    const decks = await db.decks.toArray();
    const deckIds = descendantIds(decks, id);
    const notes = (await db.notes.where('deckId').anyOf(deckIds).toArray()).filter((n) => !n.deletedAt);
    const cards = (await db.cards.where('deckId').anyOf(deckIds).toArray()).filter((c) => !c.deletedAt);
    const ops = [
      ...deckIds.map((d) => ({ kind: 'deck', id: d, patch: { deletedAt: now } })),
      ...notes.map((n) => ({ kind: 'note', id: n.id, patch: { deletedAt: now } })),
      ...cards.map((c) => ({ kind: 'card', id: c.id, patch: { deletedAt: now } })),
    ];
    await applyLocalMany(db, ops);
    return { decks: deckIds.length, notes: notes.length, cards: cards.length };
  });
}

/** Restore a deleted deck together with everything deleted in the same operation. */
export async function restoreDeck(db, id) {
  return writeTx(db, async () => {
    const deck = await db.decks.get(id);
    if (!deck?.deletedAt) return;
    const ts = deck.deletedAt;
    const decks = (await db.decks.toArray()).filter((d) => d.deletedAt === ts);
    const deckIds = decks.map((d) => d.id);
    const notes = (await db.notes.where('deckId').anyOf(deckIds).toArray()).filter((n) => n.deletedAt === ts);
    const cards = (await db.cards.where('deckId').anyOf(deckIds).toArray()).filter((c) => c.deletedAt === ts);
    const parent = deck.parentId ? await db.decks.get(deck.parentId) : null;
    const ops = [
      ...deckIds.map((d) => ({
        kind: 'deck',
        id: d,
        patch: d === id && parent?.deletedAt && parent.deletedAt !== ts
          ? { deletedAt: null, parentId: null }
          : { deletedAt: null },
      })),
      ...notes.map((n) => ({ kind: 'note', id: n.id, patch: { deletedAt: null } })),
      ...cards.map((c) => ({ kind: 'card', id: c.id, patch: { deletedAt: null } })),
    ];
    await applyLocalMany(db, ops);
  });
}

export function purgeDeck(db, id) {
  return applyLocal(db, 'deck', id, { purged: true, name: '' });
}
